<style scoped>
.row-clickable {
	cursor: pointer;
	transition: background-color 0.15s ease;
}
.row-clickable:hover {
	background-color: rgba(13, 110, 253, 0.05);
}
.row-clickable.table-active {
	background-color: rgba(13, 110, 253, 0.1);
}
</style>

<template>
	<HorizontalSplit>
		<template #top>
			<div style="position: relative; width: 100%; height: 100%">
				<mgl-map
					:map-style="mapStyleURL(ALIDADE_SMOOTH)"
					:center="mapCenter"
					:zoom="mapZoom"
					@map:moveend="onMoveEnd"
				>
					<mgl-raster-source
						source-id="planet"
						:tiles="[planetTileURL]"
						:tile-size="256"
						:maxzoom="19"
					>
						<mgl-raster-layer layer-id="planet" />
					</mgl-raster-source>

					<mgl-geo-json-source
						v-if="itemBoundsCollection"
						source-id="item-bounds"
						:data="itemBoundsCollection"
					>
						<mgl-fill-layer
							layer-id="item-bounds-fill"
							:paint="{
								'fill-color': '#0d6efd',
								'fill-opacity': 0.15,
							}"
						/>
						<mgl-line-layer
							layer-id="item-bounds-line"
							:paint="{
								'line-color': '#0d6efd',
								'line-width': 1.5,
							}"
						/>
					</mgl-geo-json-source>

					<mgl-geo-json-source
						v-if="selectedItemBounds"
						source-id="selected-item-bounds"
						:data="selectedItemBounds"
					>
						<mgl-line-layer
							layer-id="selected-item-outline"
							:paint="{
								'line-color': '#ffc107',
								'line-width': 3,
							}"
						/>
					</mgl-geo-json-source>
				</mgl-map>
			</div>
		</template>

		<template #bottom>
			<div class="p-3" v-if="loading" style="height: 100%">
				<div
					class="d-flex align-items-center justify-content-center h-100 text-muted"
				>
					<div class="text-center">
						<div class="spinner-border text-primary mb-2" role="status"></div>
						<p class="small">Loading review items...</p>
					</div>
				</div>
			</div>

			<div class="p-3" v-else-if="error">
				<div class="alert alert-danger">{{ error }}</div>
			</div>

			<div class="p-3" v-else>
				<div class="d-flex justify-content-between align-items-center mb-2">
					<h6 class="mb-0">
						<i class="bi bi-grid-3x3-gap-fill"></i>
						Corpus Items ({{ items.length }})
					</h6>
					<div class="d-flex gap-2">
						<button
							class="btn btn-sm btn-outline-success"
							:disabled="kmlDownloading"
							@click="downloadKML"
						>
							<i
								class="bi"
								:class="
									kmlDownloading
										? 'spinner-border spinner-border-sm'
										: 'bi-download'
								"
							></i>
							Export KML
						</button>
					</div>
				</div>

				<div class="table-responsive">
					<table class="table table-sm table-hover">
						<thead class="table-light">
							<tr>
								<th></th>
								<th style="width: 220px">Item ID</th>
								<th class="text-end" style="width: 100px">Pool Total</th>
								<th class="text-end" style="width: 140px">
									Pool Needing Review
								</th>
								<th class="text-end" style="width: 100px">Task Total</th>
								<th class="text-end" style="width: 140px">Task Needs Review</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="item in items"
								:key="item.id"
								class="row-clickable"
								:class="{ 'table-active': selectedItemId === item.id }"
								@click="selectItem(item)"
							>
								<td class="text-nowrap">
									<RouterLink
										:to="`/_/sudo/vision-tasks/pool-corpus/item/${item.id}`"
										class="btn btn-outline-primary btn-sm py-0"
										title="Review this item"
									>
										<i class="bi bi-play-fill"></i>
									</RouterLink>
								</td>
								<td class="font-monospace small">{{ item.id }}</td>
								<td class="text-end">{{ item.pool_total }}</td>
								<td class="text-end fw-bold text-danger">
									{{ item.pool_unreviewed }}
								</td>
								<td class="text-end">{{ item.task_total }}</td>
								<td class="text-end fw-bold text-danger">
									{{ item.task_needs_review }}
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</template>
	</HorizontalSplit>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { RouterLink } from "vue-router";
import {
	MglMap,
	MglRasterSource,
	MglRasterLayer,
	MglGeoJsonSource,
	MglFillLayer,
	MglLineLayer,
	useMap,
} from "vue-maplibre-gl";
import { mapStyleURL } from "@/map/util";
import { ALIDADE_SMOOTH } from "@/map/constants";
import HorizontalSplit from "@/components/layout/HorizontalSplit.vue";
import { apiClient } from "@/client";
import type { VisionReviewItemSummary } from "@/type/api";

const mapInstance = useMap();

const items = ref<VisionReviewItemSummary[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const kmlDownloading = ref(false);
const selectedItemId = ref<string | null>(null);
const selectedItem = ref<VisionReviewItemSummary | null>(null);
const mapCenter = ref<[number, number]>([-95, 39]);
const mapZoom = ref(4);

const planetTileURL = "/api/tile/planet/any/{z}/{x}/{y}?bands=3,2,1";

function mercXToLng(x: number): number {
	return (x / 20037508.34) * 180;
}

function mercYToLat(y: number): number {
	return (
		(180 / Math.PI) *
		(2 * Math.atan(Math.exp((y / 20037508.34) * Math.PI)) - Math.PI / 2)
	);
}

// Convert an item's EPSG:3857 raster bounds to a WGS84 polygon for map
// overlay and centering.
function itemFootprintGeoJSON(item: VisionReviewItemSummary): GeoJSON.Polygon {
	const west = mercXToLng(item.min_x);
	const east = mercXToLng(item.max_x);
	const north = mercYToLat(item.max_y);
	const south = mercYToLat(item.min_y);
	return {
		type: "Polygon",
		coordinates: [
			[
				[west, north],
				[east, north],
				[east, south],
				[west, south],
				[west, north],
			],
		],
	};
}

const itemBoundsCollection = computed(() => {
	if (items.value.length === 0) return null;
	return {
		type: "FeatureCollection" as const,
		features: items.value.map((item) => ({
			type: "Feature" as const,
			geometry: itemFootprintGeoJSON(item),
			properties: {
				itemId: item.id,
			},
		})),
	};
});

const selectedItemBounds = computed(() => {
	if (!selectedItem.value) return null;
	return itemFootprintGeoJSON(selectedItem.value);
});

function selectItem(item: VisionReviewItemSummary) {
	selectedItemId.value = item.id;
	selectedItem.value = item;

	const map = mapInstance.map;
	if (!map) return;

	const west = mercXToLng(item.min_x);
	const east = mercXToLng(item.max_x);
	const north = mercYToLat(item.max_y);
	const south = mercYToLat(item.min_y);

	map.fitBounds([west, south, east, north], {
		padding: 40,
		duration: 500,
	});
}

function onMoveEnd() {
	const map = mapInstance.map;
	if (!map) return;
	mapCenter.value = [map.getCenter().lng, map.getCenter().lat];
	mapZoom.value = map.getZoom();
}

async function fetchItems() {
	loading.value = true;
	error.value = null;
	try {
		items.value = await apiClient.JSONGet<VisionReviewItemSummary[]>(
			"/api/vision/review/items",
		);
	} catch (e: unknown) {
		const err = e as {
			response?: { data?: { message?: string } };
			message?: string;
		};
		error.value =
			err?.response?.data?.message ||
			err?.message ||
			"Failed to load review items.";
	} finally {
		loading.value = false;
	}
}

async function downloadKML() {
	kmlDownloading.value = true;
	try {
		const token = localStorage.getItem("authToken");
		const resp = await fetch("/api/vision/train/pool/export", {
			headers: token ? { Authorization: `Bearer ${token}` } : {},
		});
		if (!resp.ok) throw new Error(`Export failed: ${resp.status}`);
		const blob = await resp.blob();
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = "reviewed-pools.kml";
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	} finally {
		kmlDownloading.value = false;
	}
}

onMounted(async () => {
	await fetchItems();
});
</script>
